import React, { FC, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import ChipWithTooltip from '@components/ChipWithTooltip';
import { OperatingModelForPartners } from '@components/EntityDrawers/constants/common';
import { CommonKeys } from '@components/EntityDrawers/constants/keys';
import ListItemWithInfo from '@components/Inputs/Autocomplete/items/ListItemWithInfo';
import ControlledAutocomplete from '@components/Inputs/controllers/ControlledAutocomplete';
import { OperationalModel } from '@constants/canvas/layers';
import { MAX_VISIBLE_CHIP_SYMBOLS } from '@constants/entities/ui';
import { useProject } from '@context/Project/ProjectProvider';
import useProjectData from '@hooks/useProjectData';
import { transformArrayToOptions } from '@utils/helpers';

const PartnersList: FC = () => {
  const { viewOnly } = useProject();
  const [{ partners = [] }] = useProjectData();

  const operationalModel: OperationalModel = useWatch({
    name: CommonKeys.OperationalModel,
  });

  const partnersOptions = useMemo(() => {
    return transformArrayToOptions(partners);
  }, [partners]);

  const isPartnersEnabled = useMemo(() => {
    return OperatingModelForPartners.includes(operationalModel);
  }, [operationalModel]);

  if (!isPartnersEnabled) return null;

  return (
    <ControlledAutocomplete
      name={CommonKeys.Partners}
      label="Partners list"
      placeholder="Select partner(s)"
      readOnly={viewOnly}
      options={partnersOptions}
      itemComponent={ListItemWithInfo}
      renderTags={(value, getTagProps) =>
        value.map(({ label }, index) => {
          const { key, ...chipProps } = getTagProps({ index });
          return (
            <ChipWithTooltip
              key={key}
              label={label}
              tooltip={{
                disableHoverListener: label.length < MAX_VISIBLE_CHIP_SYMBOLS,
              }}
              chip={chipProps}
            />
          );
        })
      }
      multiple
      canFlip
    />
  );
};

export default PartnersList;
